/**
 * MediaTextOrder
 * メディア＋テキスト（.mt-be-columns.is-mediatext）のSPでの並び替え。
 * 画像列が先頭で、かつテキスト列の先頭が見出しのとき、SPは画像を見出しの直後へ移して
 * 「見出し → 画像 → 本文」にする。PCは2カラムのままなので元の列へ戻す。
 *
 * 見出しが無い段組みはここでは触らない。その並び（本文 → 画像）は
 * _p-wysiwyg.scss の flex-direction: column-reverse が受け持つ。
 *
 * 画像を抜いた列は空のまま残す（PCへ戻すときの置き場）。空でも gap のぶん隙間が出るので
 * .is-empty を付けてCSSで消す（:empty は要素間の空白テキストノードがあると効かない）。
 *
 * 幅の判定は matchMedia、発火は resize / orientationchange（ScreenInfo と同じ流儀）。
 *
 * MTが吐くのは .mt-be-columns.is-mediatext までで js- フックを足せないため、
 * 例外的に構造クラスで拾っている。
 *
 * @example
 * <div class="mt-be-columns is-mediatext" style="display:flex">
 *   <div class="mt-be-column"><figure class="mt-be-image">…</figure></div>
 *   <div class="mt-be-column"><h3>…</h3><p>…</p></div>
 * </div>
 */
export default class MediaTextOrder {
  constructor(options = {}) {
    this.options = {
      selector: '.p-wysiwyg .mt-be-columns.is-mediatext',
      updateDelay: 100,
      ...options
    };

    this.blocks = [];
    document.querySelectorAll(this.options.selector).forEach((block) => {
      const entry = this.pick(block);
      if (entry) this.blocks.push(entry);
    });
    if (!this.blocks.length) return;

    this.mql = window.matchMedia('(max-width: 767px)');
    this.isSp = null;
    this.resizeTimer = null;
    this.onResize = this.onResize.bind(this);

    this.apply();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  // 条件（画像列が先頭／次の列の先頭が見出し）を満たす段組みだけ拾う
  pick(block) {
    const columns = block.querySelectorAll(':scope > .mt-be-column');
    if (columns.length < 2) return null;

    const home = columns[0];
    const image = home.querySelector('.mt-be-image');
    if (!image) return null;

    const heading = columns[1].firstElementChild;
    if (!heading || !/^H[2-6]$/.test(heading.tagName)) return null;

    return { home, image, heading };
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.apply(), this.options.updateDelay);
  }

  apply() {
    const sp = this.mql.matches;
    // 帯をまたいだときだけ動かす。またがないリサイズでDOMを触る必要はない
    if (sp === this.isSp) return;
    this.isSp = sp;

    this.blocks.forEach(({ home, image, heading }) => {
      if (sp) {
        heading.after(image);
        home.classList.add('is-empty');
      } else {
        home.appendChild(image);
        home.classList.remove('is-empty');
      }
    });
  }

  destroy() {
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
  }
}
